/**
 * environment.ts — the lake, the fish, and the reward.
 *
 * The world the agent operates in. Owns the hidden fish preference a★_t ∈
 * [0,1]^6 (which drifts), the catch-probability function, and the visual
 * flock. The agent never sees a★_t directly — only the continuous catch
 * probability p_catch as its reward signal.
 *
 * Reward design. The fish are *at* a location (spatial), and they *prefer*
 * a colour and size. Spatial closeness dominates (the fish has to be near
 * the hook), and colour/size act as additive bonuses on top. The agent's
 * reward is the continuous p_catch ∈ [0, 1] — every cast gives gradient
 * signal. A random agent catches ~30%; a learned agent converges to ~70%.
 */

import { type Action } from './featurize';
import { sigmoid, makeRng, randn } from './linalg';

export interface LakeOptions {
  /** Spatial catch radius (in lake units, lake is 1×1). A cast within this
   *  distance of the flock center is "on target". 0.22 ≈ a quarter of the lake. */
  spatialRadius: number;
  /** How quickly colour match falls off. Smaller = pickier. */
  colorSigma: number;
  /** How quickly size match falls off. Smaller = pickier. */
  sizeSigma: number;
  /** Logit slope — how sharp the catch boundary is once spatial+feature
   *  match is combined into [0, 1]. */
  kappa: number;
  /** Per-round drift stddev on a★'s spatial components. */
  spatialDrift: number;
  /** Per-round drift stddev on a★'s colour/size components. */
  featureDrift: number;
  /** Seed for deterministic demos. */
  seed: number;
}

export const DEFAULT_LAKE_OPTIONS: LakeOptions = {
  spatialRadius: 0.22,
  colorSigma: 0.28,
  sizeSigma: 0.40,
  kappa: 5.0,
  spatialDrift: 0.01,
  featureDrift: 0.005,
  seed: 42,
};

/**
 * A single fish in the flock. Visual only — the agent interacts with the
 * flock only through the catch probability. Each fish is placed on an
 * isotropic ring around the flock centre: a radius (how far out) and an
 * angle (which direction), with a per-fish bob phase so the flock looks
 * alive. Density falls off with radius — fish cluster near the centre and
 * thin out toward the edges, mirroring the Gaussian catch-probability
 * profile in §4.
 */
export interface Fish {
  /** Offset from flock centre, as a fraction of lake size. */
  radius: number;
  /** Angle around the centre (radians). */
  angle: number;
  /** Size multiplier — outer fish are smaller. */
  sizeMul: number;
  /** Per-fish bob phase. */
  phase: number;
  /** Per-fish bob amplitude. */
  amp: number;
}

/** The full outcome of a cast — richer than the bandit's reward signal. */
export interface CastOutcome {
  /** Continuous signal the agent learns from (= p_catch). Every cast gives
   *  gradient signal — the "did the user engage, probabilistically" prediction. */
  agentReward: number;
  /** Whether a fish was hooked (Bernoulli draw of pCatch). Visual + stats. */
  caught: boolean;
  /** True p_catch this round (for regret / display only). */
  pCatch: number;
  /** Caught-fish weight in kg (0 if not caught). Scales with match (§4.2). */
  weightKg: number;
  /** Spatial closeness in [0, 1] — how near the hook landed to the flock. */
  spatial: number;
  /** Colour match in [0, 1]. */
  colorMatch: number;
  /** Size match in [0, 1]. */
  sizeMatch: number;
  /** Overall match quality in [0, 1]. Drives the caught-fish weight + size. */
  match: number;
}

export class Lake {
  opts: LakeOptions;
  /** Hidden fish preference a★_t. Drifts each round. */
  preference: Action;
  /** Visual flock — positions are derived from `preference` + per-fish offsets. */
  flock: Fish[];
  private rng: () => number;

  constructor(opts?: Partial<LakeOptions>) {
    this.opts = { ...DEFAULT_LAKE_OPTIONS, ...opts };
    this.rng = makeRng(this.opts.seed);
    this.preference = this.randomPreference();
    this.flock = this.spawnFlock();
  }

  private randomPreference(): Action {
    return {
      x: 0.2 + this.rng() * 0.6,
      y: 0.2 + this.rng() * 0.6,
      r: this.rng(),
      g: this.rng(),
      b: this.rng(),
      size: this.rng(),
    };
  }

  private spawnFlock(): Fish[] {
    // Place ~40 fish on isotropic rings around the centre. The radius is
    // drawn from a sqrt distribution so fish cluster near the centre (dense)
    // and thin out toward the edges (sparse), mirroring the Gaussian catch
    // profile. Outer fish are smaller — read as "smaller fish, more space
    // between" as you move out.
    const n = 40;
    const flock: Fish[] = [];
    for (let i = 0; i < n; i++) {
      const radius = Math.sqrt(this.rng()) * 0.30;
      const angle = this.rng() * Math.PI * 2;
      const sizeMul = 1.0 - 0.6 * (radius / 0.30);
      flock.push({
        radius,
        angle,
        sizeMul,
        phase: this.rng() * Math.PI * 2,
        amp: 0.003 + this.rng() * 0.008,
      });
    }
    return flock;
  }

  /** Let the UI (legend panel) override the hidden preference so a viewer
   *  can poke the fish and watch the agent re-learn. */
  setPreference(p: Partial<Action>): void {
    Object.assign(this.preference, p);
  }

  /**
   * Advance the hidden preference by one drift step (§5). Spatial and feature
   * drift rates are separate so we can keep colour/size stable while the fish
   * roam (or vice versa). Called once per bandit round.
   */
  driftPreference(): void {
    const sd = this.opts.spatialDrift;
    const fd = this.opts.featureDrift;
    const p = this.preference;
    p.x = clip(p.x + randn(this.rng) * sd);
    p.y = clip(p.y + randn(this.rng) * sd);
    p.r = clip(p.r + randn(this.rng) * fd);
    p.g = clip(p.g + randn(this.rng) * fd);
    p.b = clip(p.b + randn(this.rng) * fd);
    p.size = clip(p.size + randn(this.rng) * fd);
  }

  /**
   * Decompose a cast into its match components against the hidden preference.
   *   spatial   = exp(−‖(x,y) − (x★,y★)‖² / 2σ_spatial²)        ∈ [0, 1]
   *   colorMatch= exp(−‖(r,g,b) − (r★,g★,b★)‖² / 2σ_color²)     ∈ [0, 1]
   *   sizeMatch = exp(−(size − size★)² / 2σ_size²)               ∈ [0, 1]
   *
   * The overall match is a **weighted sum**, not a product: the fish has to
   * be near the hook (spatial), and colour/size act as additive bonuses on
   * top. A multiplicative `spatial · color · size` made the catch landscape
   * far too peaky — a random cast saw match ≈ 0 and the agent never got a
   * reward to learn from. The weighted form keeps spatial dominant (a cast on
   * the flock center catches fish even with the wrong colour) while still
   * rewarding colour/size convergence.
   *
   *   match = 0.6·spatial + 0.25·colorMatch + 0.15·sizeMatch   ∈ [0, 1]
   */
  matchComponents(a: Action): { spatial: number; colorMatch: number; sizeMatch: number; match: number } {
    const p = this.preference;
    const ddx = a.x - p.x;
    const ddy = a.y - p.y;
    const spatial = Math.exp(-(ddx * ddx + ddy * ddy) / (2 * this.opts.spatialRadius * this.opts.spatialRadius));
    const dr = a.r - p.r;
    const dg = a.g - p.g;
    const db = a.b - p.b;
    const colorMatch = Math.exp(
      -(dr * dr + dg * dg + db * db) / (2 * this.opts.colorSigma * this.opts.colorSigma),
    );
    const ds = a.size - p.size;
    const sizeMatch = Math.exp(-(ds * ds) / (2 * this.opts.sizeSigma * this.opts.sizeSigma));
    const match = 0.6 * spatial + 0.25 * colorMatch + 0.15 * sizeMatch;
    return { spatial, colorMatch, sizeMatch, match };
  }

  /**
   * True catch probability for an action (§4). Match ∈ [0, 1] is mapped
   * through a logistic so the boundary is smooth. At match = 0.5 the agent
   * has a 50% chance; at match = 1 it's ≈ σ(κ/2).
   *
   *   p_catch = σ( κ · (match − 0.5) )
   */
  catchProbability(a: Action): number {
    const { match } = this.matchComponents(a);
    return sigmoid(this.opts.kappa * (match - 0.5));
  }

  /**
   * Sample an observed outcome for an action and advance the world one round.
   *
   * Two separate signals come out of this:
   *   - `agentReward` — the continuous signal the bandit learns from. This is
   *     p_catch itself, so *every cast gives gradient signal* — the demo's
   *     core design decision. Binary catch/no-catch was too noisy: in a sparse
   *     landscape the agent saw <1 catch per 10 casts early and could not
   *     learn within a viewer's attention span.
   *   - `caught` — a Bernoulli draw of p_catch, kept purely for the visual
   *     splash animation and the catch/weight counters.
   *
   * Catch weight (kg) scales with the match quality (§4.2) — a centred cast
   * with matching colour/size lands a bigger fish than a scraping one.
   */
  observe(a: Action): CastOutcome {
    const pCatch = this.catchProbability(a);
    const caught = this.rng() < pCatch;
    const m = this.matchComponents(a);

    // Continuous reward the agent learns from = p_catch.
    const agentReward = pCatch;

    // Visual catch weight in kg. Scales with match quality: a perfect cast
    // (match ≈ 1) lands a ~2 kg fish; a scraping cast (match ≈ 0.3) lands ~0.3 kg.
    const weightKg = caught ? 0.3 + m.match * 1.7 : 0;

    return {
      agentReward,
      caught,
      pCatch,
      weightKg,
      spatial: m.spatial,
      colorMatch: m.colorMatch,
      sizeMatch: m.sizeMatch,
      match: m.match,
    };
  }
}

/** Clamp into [0, 1] — keeps the drifting preference inside the cube. */
function clip(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
